import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Pencil,
  Trash2,
  Star,
  FileText,
  Layers,
  ChevronDown,
  ChevronRight,
  Check,
} from "lucide-react";
import {
  releaseNoteTemplatesApi,
  sectionTypesApi,
  servicesApi,
  ReleaseNoteTemplate,
  TemplateSection,
  ReleaseNoteSectionType,
} from "../../api/client";
import { titleCase } from "../../lib/utils";
import ConfirmationModal from "../../components/ConfirmationModal";

export default function ServiceTemplatesSettings() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<ReleaseNoteTemplate | null>(null);
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  const [serviceFilter, setServiceFilter] = useState("");
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    service_id: string;
    is_default: boolean;
    sections: TemplateSection[];
  }>({
    name: "",
    description: "",
    service_id: "",
    is_default: false,
    sections: [],
  });

  const { data: templatesData, isLoading } = useQuery({
    queryKey: ["release-note-templates", serviceFilter],
    queryFn: () =>
      releaseNoteTemplatesApi.list(
        serviceFilter ? { service_id: serviceFilter } : undefined,
      ),
  });

  const { data: sectionTypesData } = useQuery({
    queryKey: ["section-types"],
    queryFn: () => sectionTypesApi.list(),
  });

  const { data: servicesData } = useQuery({
    queryKey: ["services"],
    queryFn: () => servicesApi.list(),
  });

  const templates = templatesData?.templates || [];
  const sectionTypes = sectionTypesData?.section_types || [];
  const services = servicesData?.services || [];

  // Group section types by category
  const deploymentSectionTypes = sectionTypes.filter(
    (st) => st.category === "deployment" && st.is_active,
  );
  const informationalSectionTypes = sectionTypes.filter(
    (st) => st.category === "informational" && st.is_active,
  );

  const createMutation = useMutation({
    mutationKey: ["createReleaseNoteTemplate"],
    mutationFn: releaseNoteTemplatesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release-note-templates"] });
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationKey: ["updateReleaseNoteTemplate"],
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof releaseNoteTemplatesApi.update>[1];
    }) => releaseNoteTemplatesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release-note-templates"] });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationKey: ["deleteReleaseNoteTemplate"],
    mutationFn: releaseNoteTemplatesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release-note-templates"] });
    },
  });

  const setDefaultMutation = useMutation({
    mutationKey: ["setDefaultTemplate"],
    mutationFn: releaseNoteTemplatesApi.setDefault,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["release-note-templates"] });
    },
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingTemplate(null);
    setFormData({
      name: "",
      description: "",
      service_id: "",
      is_default: false,
      sections: [],
    });
  };

  const handleEdit = (template: ReleaseNoteTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || "",
      service_id: template.service_id || "",
      is_default: template.is_default,
      sections: template.sections || [],
    });
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data = {
      name: formData.name,
      description: formData.description || undefined,
      service_id: formData.service_id,
      is_default: formData.is_default,
      sections: formData.sections,
    };

    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const toggleSection = (sectionType: ReleaseNoteSectionType) => {
    const slug = sectionType.slug;
    const exists = formData.sections.some((s) => s.section_type_slug === slug);

    if (exists) {
      setFormData((prev) => ({
        ...prev,
        sections: prev.sections.filter((s) => s.section_type_slug !== slug),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        sections: [
          ...prev.sections,
          { section_type_slug: slug, title: "", notes: "" },
        ],
      }));
    }
  };

  const isSectionSelected = (slug: string) => {
    return formData.sections.some((s) => s.section_type_slug === slug);
  };

  const getSectionTypeName = (slug: string) => {
    const st = sectionTypes.find((s) => s.slug === slug);
    return st?.name || slug;
  };

  const handleDelete = (template: ReleaseNoteTemplate) => {
    setConfirmModal({
      isOpen: true,
      title: "Delete Template",
      message: `Are you sure you want to delete the template "${template.name}"? This action cannot be undone.`,
      onConfirm: () => {
        deleteMutation.mutate(template.id);
        setConfirmModal((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Release Notes Templates
          </h1>
          <p className="text-gray-600 mt-1">
            Define which sections appear when creating release notes for each
            service
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="btn btn-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Template
        </button>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="w-64">
          <label className="label">Filter by Service</label>
          <select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="input w-full"
          >
            <option value="">All Services</option>
            {services.map((service) => (
              <option key={service.id} value={service.id}>
                {titleCase(service.name)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {editingTemplate ? "Edit Template" : "Create Template"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="input w-full"
                  placeholder="e.g., Standard Release, Hotfix, Config Only"
                  required
                />
              </div>
              <div>
                <label className="label">Service *</label>
                <select
                  value={formData.service_id}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      service_id: e.target.value,
                    }))
                  }
                  className="input w-full"
                  required
                >
                  <option value="">Select a service</option>
                  {services.map((service) => (
                    <option key={service.id} value={service.id}>
                      {titleCase(service.name)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="label">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="input w-full"
                rows={2}
                placeholder="Optional description of when to use this template"
              />
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_default}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_default: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">
                  Set as default template for this service
                </span>
              </label>
            </div>

            {/* Sections Selection */}
            <div>
              <h3 className="font-medium text-gray-900 mb-3">
                Select Sections
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Choose which sections will appear when creating release notes
                with this template. Users can still remove sections they don't
                need for a particular deployment.
              </p>

              {/* Deployment Sections */}
              {deploymentSectionTypes.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Deployment Sections
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {deploymentSectionTypes.map((st) => (
                      <button
                        key={st.id}
                        type="button"
                        onClick={() => toggleSection(st)}
                        className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-colors ${
                          isSectionSelected(st.slug)
                            ? "border-brand-500 bg-brand-50 text-brand-700"
                            : "border-gray-200 hover:border-gray-300 text-gray-700"
                        }`}
                      >
                        <div
                          className={`w-5 h-5 rounded flex items-center justify-center ${
                            isSectionSelected(st.slug)
                              ? "bg-brand-600 text-white"
                              : "border border-gray-300"
                          }`}
                        >
                          {isSectionSelected(st.slug) && (
                            <Check className="w-3 h-3" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-sm">{st.name}</div>
                          {st.description && (
                            <div className="text-xs text-gray-500 line-clamp-1">
                              {st.description}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Informational Sections */}
              {informationalSectionTypes.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Informational Sections
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {informationalSectionTypes.map((st) => (
                      <button
                        key={st.id}
                        type="button"
                        onClick={() => toggleSection(st)}
                        className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-colors ${
                          isSectionSelected(st.slug)
                            ? "border-brand-500 bg-brand-50 text-brand-700"
                            : "border-gray-200 hover:border-gray-300 text-gray-700"
                        }`}
                      >
                        <div
                          className={`w-5 h-5 rounded flex items-center justify-center ${
                            isSectionSelected(st.slug)
                              ? "bg-brand-600 text-white"
                              : "border border-gray-300"
                          }`}
                        >
                          {isSectionSelected(st.slug) && (
                            <Check className="w-3 h-3" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-sm">{st.name}</div>
                          {st.description && (
                            <div className="text-xs text-gray-500 line-clamp-1">
                              {st.description}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {formData.sections.length === 0 && (
                <div className="text-center py-6 bg-gray-50 rounded-lg mt-4">
                  <Layers className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">
                    No sections selected. Click on sections above to add them to
                    this template.
                  </p>
                </div>
              )}

              {formData.sections.length > 0 && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="text-sm font-medium text-gray-700 mb-2">
                    Selected ({formData.sections.length}):
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.sections.map((section) => (
                      <span
                        key={section.section_type_slug}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-sm"
                      >
                        {getSectionTypeName(section.section_type_slug)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={resetForm}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="btn btn-primary"
              >
                {editingTemplate ? "Update" : "Create"} Template
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Templates List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : templates.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No templates found
          </h3>
          <p className="text-gray-600 mb-4">
            Create templates to define which sections appear in release notes
          </p>
          <button onClick={() => setShowForm(true)} className="btn btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Create Template
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {templates.map((template) => {
            const isExpanded = expandedTemplate === template.id;
            const sections = template.sections || [];
            return (
              <div
                key={template.id}
                className="bg-white rounded-lg border border-gray-200"
              >
                <div
                  className="p-4 flex items-center gap-4 cursor-pointer"
                  onClick={() =>
                    setExpandedTemplate(isExpanded ? null : template.id)
                  }
                >
                  <FileText className="w-5 h-5 text-gray-400" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {template.name}
                      </span>
                      {template.is_default && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-yellow-100 text-yellow-700 text-xs">
                          <Star className="w-3 h-3" />
                          Default
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {template.service_name
                        ? titleCase(template.service_name)
                        : "Unknown Service"}{" "}
                      &bull; {sections.length} section
                      {sections.length !== 1 ? "s" : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!template.is_default && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDefaultMutation.mutate(template.id);
                        }}
                        className="p-2 text-gray-400 hover:text-yellow-600"
                        title="Set as default"
                      >
                        <Star className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(template);
                      }}
                      className="p-2 text-gray-400 hover:text-gray-600"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(template);
                      }}
                      className="p-2 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {isExpanded && sections.length > 0 && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Sections in this template
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {sections.map((section, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 rounded-md text-sm"
                        >
                          <Layers className="w-3.5 h-3.5 text-gray-400" />
                          {getSectionTypeName(section.section_type_slug)}
                        </span>
                      ))}
                    </div>
                    {template.description && (
                      <p className="text-sm text-gray-500 mt-3">
                        {template.description}
                      </p>
                    )}
                  </div>
                )}

                {isExpanded && sections.length === 0 && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50">
                    <p className="text-sm text-gray-500">
                      No sections defined for this template.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
